import React, { useState, useMemo, useEffect } from 'react';
import { formatCurrency, formatDate } from '../helpers';

const IconTable = () => (
    <svg className="w-4 h-4 text-[#01032d]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const IconChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
);

const IconChevronRight = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

const IconInfo = () => (
    <svg className="w-12 h-12 text-slate-300 stroke-[1.5] opacity-60 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

const TablePreviewFinanzas = ({ registros, columnasSeleccionadas, camposDisponibles, moduloRaiz }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(100);

    // Resetea la página al cambiar de filtro, módulo o tamaño de página
    useEffect(() => {
        setCurrentPage(1);
    }, [registros, moduloRaiz, recordsPerPage]);

    const totalPages = Math.ceil(registros.length / recordsPerPage) || 1;

    const paginatedRegistros = useMemo(() => {
        const start = (currentPage - 1) * recordsPerPage;
        return registros.slice(start, start + recordsPerPage);
    }, [registros, currentPage, recordsPerPage]);

    const renderCell = (colKey, row) => {
        const val = row[colKey];

        // 1. Badge de Estado de Auditoría de Conciliaciones
        if (colKey === 'estadoAuditoria') {
            if (row.estadoAuditoria === 'PENDIENTE') {
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 uppercase">
                        Pendiente
                    </span>
                );
            }
            if (row.tieneDiferencia) {
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-50 text-rose-700 border border-rose-200 uppercase">
                        Desfase: {formatCurrency(Math.abs(row.diferencia), row.moneda)}
                    </span>
                );
            }
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-200 uppercase">
                    ✓ Conciliado 100%
                </span>
            );
        }

        // 2. Moneda e Importes
        if (['montoIva', 'montoTotal', 'esperado', 'ingresado', 'diferencia', 'monto', 'egresoBanco'].includes(colKey)) {
            const isNegative = colKey === 'diferencia' && row.diferencia < 0;
            return (
                <span className={`font-mono font-bold tracking-tight ${isNegative ? 'text-rose-600' : 'text-slate-800'}`}>
                    {formatCurrency(val, row.moneda || 'ARS')}
                </span>
            );
        }

        // 3. Fechas
        if (colKey === 'fecha') {
            return <span className="font-mono text-slate-500 font-semibold">{formatDate(val)}</span>;
        }

        // 4. Números de Comprobante
        if (colKey === 'numeroFactura' || colKey === 'numeroComprobante') {
            return <span className="font-mono font-bold text-slate-700">{val || 'S/N'}</span>;
        }

        return String(val !== undefined && val !== null ? val : '-');
    };

    return (
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden flex flex-col h-full text-left">
            {/* CABECERA DE LA TABLA */}
            <div className="p-4 px-6 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                    <IconTable /> 
                    <span className="text-[10px] font-black text-[#01032d] uppercase tracking-widest">
                        Datos Procesados ({registros.length} registros totales)
                    </span>
                </div>
                
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Filas por página:</span>
                    <select
                        value={recordsPerPage}
                        onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                        className="bg-white border border-slate-200 rounded-lg p-1 px-2 text-[11px] font-bold text-slate-600 outline-none cursor-pointer"
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* CUERPO DE LA TABLA */}
            <div className="flex-grow overflow-auto custom-scrollbar relative bg-white">
                <table className="w-full text-xs table-auto border-collapse min-w-max">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] font-black border-b tracking-wider sticky top-0 z-20 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                        <tr>
                            {columnasSeleccionadas.map(colKey => {
                                let defCampo;
                                Object.values(camposDisponibles).forEach(seccion => {
                                    const match = seccion.find(c => c.key === colKey);
                                    if (match) defCampo = match;
                                });
                                const labelCabecera = defCampo ? defCampo.label : colKey;
                                const isNumeric = ['montoIva', 'montoTotal', 'esperado', 'ingresado', 'diferencia', 'monto', 'egresoBanco'].includes(colKey);

                                return (
                                    <th 
                                        key={colKey} 
                                        className={`p-3.5 px-5 font-black bg-slate-50 text-slate-500 select-none truncate ${
                                            isNumeric ? 'text-right' : colKey === 'estadoAuditoria' ? 'text-center' : 'text-left'
                                        }`}
                                    >
                                        {labelCabecera}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                        {paginatedRegistros.length === 0 ? (
                            <tr>
                                <td colSpan={columnasSeleccionadas.length || 1} className="p-16 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <IconInfo />
                                        <p className="text-slate-500 font-bold uppercase tracking-wider text-xs">No se encontraron registros</p>
                                        <p className="text-[11px] text-slate-400 mt-1 max-w-sm mx-auto leading-relaxed">
                                            Prueba cambiando los parámetros de segmentación temporal o removiendo filtros del panel lateral.
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedRegistros.map((row, idx) => (
                                <tr key={row.id || idx} className="hover:bg-slate-50/40 transition-colors duration-150">
                                    {columnasSeleccionadas.map(colKey => {
                                        const isNumeric = ['montoIva', 'montoTotal', 'esperado', 'ingresado', 'diferencia', 'monto', 'egresoBanco'].includes(colKey);
                                        return (
                                            <td 
                                                key={colKey} 
                                                className={`p-3 px-5 truncate ${
                                                    isNumeric ? 'text-right' : colKey === 'estadoAuditoria' ? 'text-center' : 'text-left'
                                                }`}
                                            >
                                                {renderCell(colKey, row)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* PIE DE PAGINACIÓN COMPLETA */}
            {totalPages > 1 && (
                <div className="h-14 shrink-0 bg-slate-50 border-t border-slate-200/80 flex justify-between items-center px-8 text-xs font-bold text-slate-500">
                    <span className="text-slate-500">
                        Mostrando página <span className="text-slate-700 font-black">{currentPage}</span> de <span className="text-slate-700 font-black">{totalPages}</span> (<span className="text-slate-600">{paginatedRegistros.length}</span> de <span className="text-slate-600">{registros.length}</span> filas)
                    </span>
                    
                    <div className="flex items-center gap-2">
                        {/* Botón Anterior */}
                        <button 
                            type="button"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                            disabled={currentPage === 1} 
                            className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-30 uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 active:scale-95 cursor-pointer"
                        >
                            <IconChevronLeft /> Anterior
                        </button>
                        
                        {/* Selector Numérico de Páginas */}
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum = i + 1;
                                if (currentPage > 3 && totalPages > 5) {
                                    pageNum = currentPage - 3 + i;
                                    if (pageNum + (4 - i) > totalPages) {
                                        pageNum = totalPages - 4 + i;
                                    }
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        type="button"
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${
                                            currentPage === pageNum 
                                                ? 'bg-[#01032d] text-white shadow-xs' 
                                                : 'bg-white hover:bg-slate-100 text-slate-600 border border-slate-200'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Botón Siguiente */}
                        <button 
                            type="button"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                            disabled={currentPage === totalPages} 
                            className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-30 uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 active:scale-95 cursor-pointer"
                        >
                            Siguiente <IconChevronRight />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TablePreviewFinanzas;